import { Chip, Menu, MenuItem } from "@mui/material";
import { GridApiCommon } from "@mui/x-data-grid";
import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbBuildingAirport, TbPlane } from "react-icons/tb";

import FilterButton from "@/components/table/FilterButton";
import { Airports } from "@/constants";
import { usePlanActions, usePlanFilters } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

import { CommonFilterSwitch } from "../../types";
import { MemoizedCell } from "./PlanAirportColumn";

enum ListEnums {
    FIRST,
    SECOND,
    THIRD,
    FOURTH,
}

const options = [
    {
        isArrival: CommonFilterSwitch.TRUE,
        airport: Airports.IST,
        val: ListEnums.FIRST,
    },
    {
        isArrival: CommonFilterSwitch.TRUE,
        airport: Airports.SAW,
        val: ListEnums.SECOND,
    },
    {
        isArrival: CommonFilterSwitch.FALSE,
        airport: Airports.IST,
        val: ListEnums.THIRD,
    },
    {
        isArrival: CommonFilterSwitch.FALSE,
        airport: Airports.SAW,
        val: ListEnums.FOURTH,
    },
];

function PlanToolbarAirport({
    rows,
    apiRef,
    disabled = false,
}: {
    rows: IPlanPassenger<"api">[];
    apiRef: MutableRefObject<GridApiCommon>;
    disabled?: boolean;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const { updateFilters } = usePlanActions();
    const filters = usePlanFilters();
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);

    const { t } = useTranslation();
    const handleFilterClick = useCallback(
        (filter: { isArrival: CommonFilterSwitch; airport: Airports | null }) => {
            updateFilters(
                { ...filters, ...filter, cancelled: CommonFilterSwitch.ALL, hideSelfTransport: false },
                apiRef.current
            );
        },
        [updateFilters]
    );

    const totals = useMemo(() => {
        const totals = {
            [ListEnums.FIRST]: 0,
            [ListEnums.SECOND]: 0,
            [ListEnums.THIRD]: 0,
            [ListEnums.FOURTH]: 0,
        };
        for (const passenger of rows) {
            for (const { isArrival, airport, val } of options) {
                if (
                    (!isArrival ||
                        passenger.isFlightArrival === (isArrival === CommonFilterSwitch.FALSE ? false : true)) &&
                    (!airport || passenger.airport === airport)
                )
                    totals[val] += 1;
            }
        }
        return totals;
    }, [rows, options]);

    return (
        <>
            <FilterButton
                active={!!filters.airport}
                handleClick={handleClick}
                icon={TbBuildingAirport}
                disabled={disabled}
                tooltip={t("tooltips.filterByAirport")}
            />
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem
                    selected={filters.isArrival === CommonFilterSwitch.ALL && filters.airport === null}
                    className="!flex !items-center !justify-start !gap-2"
                    dense
                    onClick={() => handleFilterClick({ isArrival: CommonFilterSwitch.ALL, airport: null })}
                >
                    <div className="flex items-center h-full py-1">
                        <Chip icon={<TbPlane fontSize={16} />} label={"Hepsi"} />
                    </div>
                </MenuItem>

                {options.map((item, index) => (
                    <MenuItem
                        key={index}
                        selected={filters.isArrival === item.isArrival && filters.airport === item.airport}
                        className="!flex !items-center !justify-between !gap-10 "
                        dense
                        onClick={() => handleFilterClick(item)}
                    >
                        <MemoizedCell
                            value={item.airport}
                            isFlightArrival={item.isArrival === CommonFilterSwitch.FALSE ? false : true}
                        />
                        <span className="font-medium ">{totals[item.val]}</span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default PlanToolbarAirport;
